import * as CL from '@design-system/component-library';
import { Grid } from '../Grid/Grid';
import { JsonLd } from 'react-schemaorg';
import { MobileCarouselWrapper, getItemList, getOffer, getSubscription } from './SubscriptionUtils';
import { OfferSpecifications } from './OfferSpecifications';
import { SiteContext } from '../../public/site/SiteContext';
import { SubscriptionCard } from './SubscriptionCard';
import { addToShoppingBasket } from '../ShoppingBasket/shoppingBasketFunctions';
import {
  additionalServicesMsg,
  elisaMatkapuhelinvastaajaMsg,
  euMsg,
  exploreAndOrderMsg,
  finlandNordicsAndBalticsMsg2,
  internetConnectionMsg,
  mBpsMsg,
  outsideEuMsg,
  pcsMsg,
  speedMsg,
  standalone5GMsg,
  t,
  textMessagesMsg,
  unlimitedInternetInFinlandNordicBalticMsg,
  unlimitedMsg,
  uploadSpeedMsg,
  voiceAndTextMessagesMsg,
  voiceMsg,
  voiceSubscriptionMsg,
} from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils';
import { dsClass } from '../../common/constants/dsClasses';
import { getViewItemListAnalyticsEvent } from '../SubscriptionCards/subscriptionCardsAnalyticsUtils';
import { handleOrder } from './OrderUtils';
import {
  itsenainen5GVerkkoContent,
  matkapuhelinvastaajaContent,
  mobiiliturvaContent,
  reissaajaContent,
  reissunetti1GtContent,
  reissunetti2GtContent,
} from './HorizontalSubscriptionsContents';
import { paths } from '../../common/constants/pathVariables';
import { pushToDataLayer } from '../../common/analytics';
import { useContext, useRef } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { ItemList } from 'schema-dts';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { OrderProps, SalesProductsOrderProps, SubscriptionCardWithModel } from './types';
import type { OrderVoiceSubLoaderData, SubscriptionUpdateLoaderData } from '../../common/loaders';
import type { State } from '../../selfservice/common/store';
import type { SubCardWithAnalyticsV4Props } from '../SubscriptionCards/subscriptionCardsAnalyticsUtils';

export interface HorizontalSubscriptionsProps {
  subscriptionCards: SubscriptionCardWithModel[];
  publicPages: boolean;
  listName: string;
  isChangeOffer?: boolean;
  offerAndFeeMap?: OfferProductMapType[];
}

export const HorizontalVoiceSubscriptions = ({
  subscriptionCards,
  publicPages,
  listName,
  isChangeOffer = false,
  offerAndFeeMap = [],
}: HorizontalSubscriptionsProps) => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const subscriptionLoaderData = useLoaderData() as SubscriptionUpdateLoaderData;
  const noeLoaderData = useLoaderData() as OrderVoiceSubLoaderData;
  const { siteBaseUrl } = useContext(SiteContext);
  const sharedProps = { publicPages, isChangeOffer, listName };

  const hasRunOnceRef = useRef(false);
  if (!hasRunOnceRef.current && subscriptionCards.every(subCard => subCard.onlineModel?.onlineModelName)) {
    pushToDataLayer(getViewItemListAnalyticsEvent(subscriptionCards as SubCardWithAnalyticsV4Props[], listName));
    hasRunOnceRef.current = true;
  }

  const { campaignsFromVoucher = [], campaignContextsFromVoucher = [] } = useSelector((reduxState: State) => {
    const campaigns = reduxState.campaigns;
    return {
      campaignsFromVoucher: campaigns?.campaigns,
      campaignContextsFromVoucher: campaigns?.campaignContexts,
      voucher: campaigns?.voucher,
    };
  }, deepEqual);

  const onSalesProductOrder = ({
    onlineModel,
    quantity,
    isAuthenticated,
    isNewNumber,
    offers,
  }: SalesProductsOrderProps) => {
    if (!offers) {
      return;
    }
    const offer = getOffer(
      isNewNumber ? offers[0].salesProduct.newNumberOfferCode : offers[0].salesProduct.oldNumberOfferCode,
      onlineModel.offers
    );
    if (offer) {
      addToShoppingBasket(onlineModel, offer, offer.commercialProducts[0], isAuthenticated, quantity, []);
    }
  };

  const onOrder = ({ onlineModel, isNewNumber, offers, quantity, voucherCode, services }: OrderProps) => {
    handleOrder({
      onlineModel,
      navigate,
      search,
      isChangeOffer,
      isNewNumber,
      quantity,
      voucherCode,
      services,
      offerAndFeeMap,
      campaignsFromVoucher,
      campaignContextsFromVoucher,
      offers,
      addOnRules: noeLoaderData.addOnRules,
      subscription: subscriptionLoaderData.subscription,
      confirmationPath: paths.PS_MOBILE_SUBSCRIPTION_UPDATE_CONFIRMATION,
      productInfoPath: paths.PS_MOBILE_SUBSCRIPTION_NEW_PRODUCT_INFO,
    });
  };

  const onOrderClick = publicPages ? onSalesProductOrder : onOrder;

  let analyticsIndex5gPlus = 0;
  let analyticsIndex5g = 0;

  const Sub600m = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription(subscriptionCards, 'perus600')}
      {...sharedProps}
      analyticsIndex={analyticsIndex5gPlus++}
      onOrder={onOrderClick}
      badges={[
        <CL.Badge key={1} color="5g" text="5G+" />,
        <CL.Badge key={2} color="blue-light" text={t.GPDS('Most popular')} />,
      ]}
      subHeader={t.N7KF(voiceSubscriptionMsg)}
      header="Perus 5G+ 600 M"
      description={t.DASH(
        'For effortless basic use, including video calls. 5G+ connection saves your phone’s battery and makes browsing safer, among other benefits.'
      )}
      infoItems={[
        { icon: 'disturbance', text: t.WXU0('Speed {} Mbps', '600') },
        { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '66,5'),
        },
      ]}
      hasQuantity={false}
      buttonText={t.SDWF(exploreAndOrderMsg)}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: elisaMatkapuhelinvastaajaMsg,
          popoverContent: matkapuhelinvastaajaContent(),
        },
      ]}
      modalDescriptionItems={[
        {
          title: t.VY1K(voiceAndTextMessagesMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.XBSA(speedMsg),
                  value: t.IJNS(mBpsMsg, '600'),
                },
                {
                  name: t.CQQ7(uploadSpeedMsg),
                  value: t.IJNS(mBpsMsg, '75'),
                },
                {
                  name: t.PV8D(voiceMsg),
                  value: '5000 min',
                },
                {
                  name: t.MXR5(textMessagesMsg),
                  value: `200 ${t.B3MG(pcsMsg)}`,
                },
              ]}
            />
          ),
        },
        {
          title: t.LXSR(additionalServicesMsg),
          content: <p className={dsClass.TEXT_XS}>{elisaMatkapuhelinvastaajaMsg}</p>,
        },
        {
          title: t.B10B(internetConnectionMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.OMY8(finlandNordicsAndBalticsMsg2),
                  value: t.MVTD(unlimitedMsg),
                },
                {
                  name: t.ZFMI(euMsg),
                  value: t.M09Z('{} GB', '66,5'),
                },
                {
                  name: t.DTQ6(outsideEuMsg),
                  value: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
                },
              ]}
            />
          ),
        },
        {
          title: '5G+',
          content: (
            <p className={dsClass.TEXT_XS}>
              {t.A504(
                '5G+ subscriptions always use the best available connection. In areas without 5G coverage, they switch to 4G network instead. Using a 5G+ subscription can cut delay times by half. The new 5G+ technology also saves your phone’s battery, which makes it a more energy-efficient option.'
              )}
            </p>
          ),
        },
      ]}
    />
  );

  const Sub1000m = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription(subscriptionCards, 'premium')}
      {...sharedProps}
      onOrder={onOrderClick}
      analyticsIndex={analyticsIndex5gPlus++}
      badges={[<CL.Badge key={1} color="5g" text="5G+" />]}
      subHeader={t.N7KF(voiceSubscriptionMsg)}
      header="Premium 5G+ 1000 M"
      description={t.TYJR(
        'The ideal option for heavy data use, including videogaming. 5G+ connection saves your phone’s battery and makes browsing safer, among other benefits.'
      )}
      infoItems={[
        { icon: 'disturbance', text: t.WXU0('Speed {} Mbps', '1000') },
        { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '80'),
        },
      ]}
      hasQuantity={false}
      buttonText={t.SDWF(exploreAndOrderMsg)}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 2${t.C4YQ('GB')}`,
          popoverContent: reissunetti2GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: elisaMatkapuhelinvastaajaMsg,
          popoverContent: matkapuhelinvastaajaContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
      modalDescriptionItems={[
        {
          title: t.VY1K(voiceAndTextMessagesMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.XBSA(speedMsg),
                  value: t.IJNS(mBpsMsg, '1000'),
                },
                {
                  name: t.CQQ7(uploadSpeedMsg),
                  value: t.IJNS(mBpsMsg, '100'),
                },
                {
                  name: t.PV8D(voiceMsg),
                  value: '5000 min',
                },
                {
                  name: t.MXR5(textMessagesMsg),
                  value: `500 ${t.B3MG(pcsMsg)}`,
                },
              ]}
            />
          ),
        },
        {
          title: t.LXSR(additionalServicesMsg),
          content: <p className={dsClass.TEXT_XS}>{elisaMatkapuhelinvastaajaMsg}</p>,
        },
        {
          title: t.B10B(internetConnectionMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.OMY8(finlandNordicsAndBalticsMsg2),
                  value: t.MVTD(unlimitedMsg),
                },
                {
                  name: t.ZFMI(euMsg),
                  value: t.M09Z('{} GB', '80'),
                },
                {
                  name: t.DTQ6(outsideEuMsg),
                  value: `Reissunetti 2${t.C4YQ('GB')}`,
                },
              ]}
            />
          ),
        },
        {
          title: '5G+',
          content: (
            <p className={dsClass.TEXT_XS}>
              {t.A504(
                '5G+ subscriptions always use the best available connection. In areas without 5G coverage, they switch to 4G network instead. Using a 5G+ subscription can cut delay times by half. The new 5G+ technology also saves your phone’s battery, which makes it a more energy-efficient option.'
              )}
            </p>
          ),
        },
      ]}
    />
  );

  const Sub300m = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription(subscriptionCards, 'perus300')}
      {...sharedProps}
      onOrder={onOrderClick}
      analyticsIndex={analyticsIndex5g++}
      badges={[]}
      subHeader={t.N7KF(voiceSubscriptionMsg)}
      header="Perus 5G, 300 M"
      description={t.S3Z6('For effortless basic use, including video calls.')}
      infoItems={[
        { icon: 'disturbance', text: t.WXU0('Speed {} Mbps', '300') },
        { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '58,5'),
        },
      ]}
      hasQuantity={false}
      buttonText={t.SDWF(exploreAndOrderMsg)}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: elisaMatkapuhelinvastaajaMsg,
          popoverContent: matkapuhelinvastaajaContent(),
        },
      ]}
      modalDescriptionItems={[
        {
          title: t.VY1K(voiceAndTextMessagesMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.XBSA(speedMsg),
                  value: t.IJNS(mBpsMsg, '300'),
                },
                {
                  name: t.CQQ7(uploadSpeedMsg),
                  value: t.IJNS(mBpsMsg, '50'),
                },
                {
                  name: t.PV8D(voiceMsg),
                  value: '5000 min',
                },
                {
                  name: t.MXR5(textMessagesMsg),
                  value: `200 ${t.B3MG(pcsMsg)}`,
                },
              ]}
            />
          ),
        },
        {
          title: t.LXSR(additionalServicesMsg),
          content: <p className={dsClass.TEXT_XS}>{elisaMatkapuhelinvastaajaMsg}</p>,
        },
        {
          title: t.B10B(internetConnectionMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.OMY8(finlandNordicsAndBalticsMsg2),
                  value: t.MVTD(unlimitedMsg),
                },
                {
                  name: t.ZFMI(euMsg),
                  value: t.M09Z('{} GB', '58,5'),
                },
                {
                  name: t.DTQ6(outsideEuMsg),
                  value: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
                },
              ]}
            />
          ),
        },
        {},
      ]}
    />
  );

  const Sub50m = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription(subscriptionCards, 'mini50')}
      {...sharedProps}
      onOrder={onOrderClick}
      analyticsIndex={analyticsIndex5g++}
      badges={[]}
      subHeader={t.N7KF(voiceSubscriptionMsg)}
      header="Mini 5G, 50 M"
      description={t.G5QH('For light browsing and low data use, such as messaging.')}
      infoItems={[
        { icon: 'disturbance', text: t.WXU0('Speed {} Mbps', '50') },
        { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '47'),
        },
      ]}
      hasQuantity={false}
      buttonText={t.SDWF(exploreAndOrderMsg)}
      additionalFeatures={[{ icon: 'check', text: 'Reissaaja', popoverContent: reissaajaContent() }]}
      modalDescriptionItems={[
        {
          title: t.VY1K(voiceAndTextMessagesMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.XBSA(speedMsg),
                  value: t.IJNS(mBpsMsg, '50'),
                },
                {
                  name: t.CQQ7(uploadSpeedMsg),
                  value: t.IJNS(mBpsMsg, '25'),
                },
                {
                  name: t.PV8D(voiceMsg),
                  value: '100 min',
                },
                {
                  name: t.MXR5(textMessagesMsg),
                  value: `100 ${t.B3MG(pcsMsg)}`,
                },
              ]}
            />
          ),
        },
        {
          title: t.LXSR(additionalServicesMsg),
          content: <p className={dsClass.TEXT_XS}>{elisaMatkapuhelinvastaajaMsg}</p>,
        },
        {
          title: t.B10B(internetConnectionMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.OMY8(finlandNordicsAndBalticsMsg2),
                  value: t.MVTD(unlimitedMsg),
                },
                {
                  name: t.ZFMI(euMsg),
                  value: t.M09Z('{} GB', '47'),
                },
                {
                  name: t.DTQ6(outsideEuMsg),
                  value: `Reissunetti 500 ${t.PZOF('MB')}`,
                },
              ]}
            />
          ),
        },
        {},
      ]}
    />
  );

  const Sub10m = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription(subscriptionCards, 'mini10')}
      {...sharedProps}
      onOrder={onOrderClick}
      analyticsIndex={analyticsIndex5g++}
      badges={[]}
      subHeader={t.N7KF(voiceSubscriptionMsg)}
      header="Mini 5G, 10 M"
      description={t.G5QH('For light browsing and low data use, such as messaging.')}
      infoItems={[
        { icon: 'disturbance', text: t.WXU0('Speed {} Mbps', '10') },
        { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '45,5'),
        },
      ]}
      hasQuantity={false}
      buttonText={t.SDWF(exploreAndOrderMsg)}
      additionalFeatures={[{ icon: 'check', text: 'Reissaaja', popoverContent: reissaajaContent() }]}
      modalDescriptionItems={[
        {
          title: t.VY1K(voiceAndTextMessagesMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.XBSA(speedMsg),
                  value: t.IJNS(mBpsMsg, '10'),
                },
                {
                  name: t.CQQ7(uploadSpeedMsg),
                  value: t.IJNS(mBpsMsg, '5'),
                },
                {
                  name: t.PV8D(voiceMsg),
                  value: '100 min',
                },
                {
                  name: t.MXR5(textMessagesMsg),
                  value: `100 ${t.B3MG(pcsMsg)}`,
                },
              ]}
            />
          ),
        },
        {
          title: t.LXSR(additionalServicesMsg),
          content: <p className={dsClass.TEXT_XS}>{elisaMatkapuhelinvastaajaMsg}</p>,
        },
        {
          title: t.B10B(internetConnectionMsg),
          content: (
            <OfferSpecifications
              specifications={[
                {
                  name: t.OMY8(finlandNordicsAndBalticsMsg2),
                  value: t.MVTD(unlimitedMsg),
                },
                {
                  name: t.ZFMI(euMsg),
                  value: t.M09Z('{} GB', '45,5'),
                },
                {
                  name: t.DTQ6(outsideEuMsg),
                  value: `Reissunetti 500 ${t.PZOF('MB')}`,
                },
              ]}
            />
          ),
        },
        {},
      ]}
    />
  );

  // This is because the current offer shouldn't be shown when changing the offer to another
  const shownCardList = subscriptionCards.map(subscriptionCard => subscriptionCard.cardName || '');

  return (
    <Grid colWidthL={publicPages ? 10 : 12}>
      <div className="of-subscriptions-main-container">
        <h2 className="of-subscriptions-main-container-carousel-title">{t.Y6ZI('Comprehensive 5G+ subscriptions')}</h2>
        <MobileCarouselWrapper>
          {shownCardList.includes('perus600') && <Sub600m />}
          {shownCardList.includes('premium') && <Sub1000m />}
        </MobileCarouselWrapper>
        <h2 className="of-subscriptions-main-container-carousel-title">{t.PN6W('5G subscriptions')}</h2>
        <MobileCarouselWrapper>
          {shownCardList.includes('perus300') && <Sub300m />}
          {shownCardList.includes('mini50') && <Sub50m />}
          {shownCardList.includes('mini10') && <Sub10m />}
        </MobileCarouselWrapper>
        <JsonLd<ItemList> item={getItemList(subscriptionCards, siteBaseUrl, pathname)} />
      </div>
    </Grid>
  );
};
